import {
  DefaultError,
  useInfiniteQuery,
  useMutation,
  useQuery,
} from '@tanstack/react-query';
import { useInfiniteHandler, useQueryToast } from 'hooks';
import i18next from 'i18next';
import { apiClient } from 'utils';
import { getDefaultInfinitePageParam } from 'utils/service/defaultInfinitePageParam';
import { useResponseHandler } from 'utils/service/responseHandler';
import {
  ICreateVideo,
  IUpdateVideo,
  IVideoPageResponse,
  IVideoResponse,
  IVideoSearch,
  IVideosResponse,
} from './types';

export const useGetVideosListInfinite = (params: IVideoSearch) => {
  const { responseHandler } = useResponseHandler();
  const { getNextPageParam, infiniteHandler } = useInfiniteHandler();

  const { itemCount, page } = getDefaultInfinitePageParam(
    params.page,
    params.itemCount
  );

  return useInfiniteQuery<IVideosResponse[], DefaultError>({
    queryKey: ['videos', params],
    queryFn: async ({ pageParam }) => {
      const response = await apiClient({
        method: 'GET',
        url: 'Video/Search',
        params: {
          ...JSON.parse(JSON.stringify(params)),
          itemCount,
          page: pageParam,
        },
      });

      const value = await responseHandler<IVideoPageResponse>(response, {
        groupName: `useGetVideosListInfinite - Page: ${pageParam}`,
      });

      return infiniteHandler(value);
    },
    initialPageParam: page,
    getNextPageParam: getNextPageParam,
  });
};

export const useGetVideo = (id: string, enabled?: boolean) => {
  const { responseHandler } = useResponseHandler();
  return useQuery<IVideoResponse, DefaultError>({
    queryKey: ['video', id],
    async queryFn() {
      const response = await apiClient({
        method: 'GET',
        url: `Video/${id}`,
      });

      return responseHandler(response, {
        groupName: `useGetVideo - ${id}`,
        notFoundPath: '/video',
      });
    },
    enabled: enabled,
  });
};

export const usePostVideo = () => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast('usePostVideo');
  return useMutation<IVideoResponse, DefaultError, ICreateVideo>({
    mutationFn: async body => {
      loading(
        i18next.t('service-message.create.loading', {
          what: body.mainTitle.title ?? i18next.t('video.what'),
        })
      );

      const response = await apiClient({ method: 'POST', url: 'Video', body });

      return responseHandler(response, {
        groupName: `usePostVideo`,
        notFoundPath: '/video',
      });
    },
    onSuccess: data => {
      success(
        i18next.t('service-message.create.success', {
          what: data.mainTitle.title ?? i18next.t('video.what'),
        })
      );
    },
    onError: dismiss,
  });
};

export const usePutVideo = () => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast('usePutVideo');

  return useMutation<IVideoResponse, DefaultError, IUpdateVideo>({
    mutationFn: async body => {
      loading(
        i18next.t('service-message.update.loading', {
          what: body.mainTitle.title ?? i18next.t('video.what'),
        })
      );
      const response = await apiClient({ method: 'PUT', url: 'Video', body });

      return responseHandler(response, {
        groupName: `usePutVideo`,
        notFoundPath: '/video',
      });
    },
    onSuccess: data => {
      success(
        i18next.t('service-message.update.success', {
          what: data.mainTitle.title ?? i18next.t('video.what'),
        })
      );
    },
    onError: dismiss,
  });
};

export const useDeleteVideo = (id: string) => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast('useDeleteVideo');

  return useMutation<{}, DefaultError>({
    mutationFn: async () => {
      loading(
        i18next.t('service-message.delete.loading', {
          what: i18next.t('video.what'),
        })
      );
      const response = await apiClient({
        method: 'DELETE',
        url: `Video/${id}`,
      });

      return responseHandler(response, {
        groupName: 'useDeleteVideo',
        notFoundPath: '/video',
      });
    },
    onSuccess: data => {
      success(
        i18next.t('service-message.delete.success', {
          what: i18next.t('video.what'),
        })
      );
    },
    onError: dismiss,
  });
};
